import { BlockStack, Box, Typography } from '@pv-frontend/ui-components-web';
import {
    atCopy,
    axisLabel,
    axisOffSourceCopy,
    baselineMovedCopy,
    engagementEventCopy,
    PREFERENCES_UNKNOWN_COPY,
    moverCopy,
    pendingCopy,
    planCopy,
    priceFirmCopy,
    rateCopy,
    readWouldWriteCopy,
    storedStateCopy,
    timesShownCopy,
    truncationCopy,
} from './home-feed-activity-copy';
import type {
    HomeFeedEngagementView,
    HomeFeedWildcardsView,
    ResultsChangedView,
} from './home-feed-activity-view';

function Label({ children }: { children: string }) {
    return (
        <Typography variant="body-sm" fontWeight="medium" tone="text-secondary">
            {children}
        </Typography>
    );
}

function Line({ children, testID }: { children: string; testID?: string }) {
    return (
        <Typography variant="body-sm" testID={testID}>
            {children}
        </Typography>
    );
}

/** Wildcard pacing, the axes the member switched off, and whether price counts as firm. Read-only. */
export function HomeFeedWildcards({ wildcards }: { wildcards: HomeFeedWildcardsView }) {
    const { pacing, axesOff, priceFirm } = wildcards;
    const planned = wildcards.planState === 'PLANNED';
    return (
        <Box testID="home-feed-wildcards">
            <BlockStack gap="100">
                <Label>Wildcards</Label>
                <Typography variant="body-md" testID="home-feed-wildcard-plan">
                    {planCopy(wildcards.planState, wildcards.notPlannedReason)}
                </Typography>
                <Label>{planned ? 'Pace' : 'Pace if the feed plans wildcards'}</Label>
                <Line>{timesShownCopy(pacing.timesShown, pacing.lookbackDays)}</Line>
                <Line testID="home-feed-wildcard-rate">
                    {rateCopy(pacing.rate, pacing.oneIn, pacing.baseRate, pacing.rateState)}
                </Line>
                <Line>
                    {`Active axes: ${pacing.activeAxes.length === 0 ? 'none' : pacing.activeAxes.map(axisLabel).join(', ')}.`}
                </Line>
                {pacing.paused.map((p) => (
                    <Line key={p.axis}>{`Paused: ${axisLabel(p.axis)} until ${atCopy(p.untilUtc)}.`}</Line>
                ))}
                {pacing.recent.length > 0 ? (
                    <Box testID="home-feed-wildcard-recent">
                    <BlockStack gap="050">
                        <Label>{`Recent outcomes, oldest first (the rate weighs the last ${pacing.rateWindow})`}</Label>
                        {pacing.recent.map((o, i) => (
                            <Line key={`${o.axis}:${o.atUtc}:${i}`}>
                                {`${axisLabel(o.axis)} · ${o.skipped ? 'ignored' : 'engaged with'} · ${atCopy(o.atUtc)}`}
                            </Line>
                        ))}
                    </BlockStack>
                    </Box>
                ) : null}
                <Label>Axes switched off</Label>
                {axesOff.length === 0 ? (
                    <Line testID="home-feed-axes-off-none">No axes are switched off.</Line>
                ) : (
                    axesOff.map((a) => (
                        <Line key={a.axis} testID="home-feed-axis-off">
                            {`${axisLabel(a.axis)}: ${axisOffSourceCopy(a.source)}, since ${atCopy(a.sinceUtc)}.`}
                        </Line>
                    ))
                )}
                <Line testID="home-feed-price-firm">{priceFirmCopy(priceFirm)}</Line>
            </BlockStack>
        </Box>
    );
}

/** The Results changed card the next read owes, or none pending with the stored baseline state. */
export function HomeFeedResultsChanged({ results }: { results: ResultsChangedView }) {
    return (
        <Box testID="home-feed-results-changed">
            <BlockStack gap="100">
                <Label>Results changed: next read (decided now)</Label>
                <Typography variant="body-md" testID="home-feed-results-pending">
                    {pendingCopy(results.pending)}
                </Typography>
                {results.card ? (
                    <Box testID="home-feed-results-card">
                    <BlockStack gap="050">
                        <Typography variant="body-md" fontWeight="medium">
                            {results.card.title}
                        </Typography>
                        {results.card.movers.map((m) => (
                            <Line key={m.listingId} testID="home-feed-results-mover">
                                {moverCopy(m)}
                            </Line>
                        ))}
                    </BlockStack>
                    </Box>
                ) : null}
                <Line testID="home-feed-results-write">{readWouldWriteCopy(results.readWouldWrite, results.pending)}</Line>
                <Label>Stored baseline</Label>
                <Line testID="home-feed-results-stored">
                    {storedStateCopy(results.state, results.cardNotedSinceUtc, results.cardLapsesUtc)}
                </Line>
                <Line testID="home-feed-results-baseline">{baselineMovedCopy(results.baselineMovedUtc)}</Line>
                {results.preferencesChangedSinceBaseline === null ? (
                    <Line testID="home-feed-results-prefs-unknown">{PREFERENCES_UNKNOWN_COPY}</Line>
                ) : results.preferencesChangedSinceBaseline ? (
                    <Line>Preferences have changed since the baseline was taken.</Line>
                ) : null}
            </BlockStack>
        </Box>
    );
}

/** The member's recent engagement on this Search, newest first, capped. */
export function HomeFeedEngagement({ engagement }: { engagement: HomeFeedEngagementView }) {
    return (
        <Box testID="home-feed-engagement">
            <BlockStack gap="100">
                <Label>{`Recent engagement (newest first, last ${engagement.windowDays} days)`}</Label>
                {engagement.events.length === 0 ? (
                    <Line testID="home-feed-engagement-none">No engagement in this window.</Line>
                ) : (
                    engagement.events.map((e, i) => (
                        <Line key={`${e.kind}:${e.occurredUtc}:${e.listingId}:${i}`} testID="home-feed-engagement-event">
                            {engagementEventCopy(e)}
                        </Line>
                    ))
                )}
                {engagement.truncated ? (
                    <Typography variant="body-sm" tone="text-secondary" testID="home-feed-engagement-truncated">
                        {truncationCopy(engagement.cap, engagement.windowDays)}
                    </Typography>
                ) : null}
            </BlockStack>
        </Box>
    );
}
